import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, Link as LinkIcon } from 'lucide-react';
import filesApi from '../../../services/apis/files';
import { Progress, addToast, Button, Input, Select, SelectItem } from '@heroui/react';
import {
  FILE_TYPE_CATEGORIES,
  FileTypeCategory,
  AcceptedFileType,
  getMimeTypeFromCategory,
  validateUrlFileType,
  validateYouTubeUrl,
  getFileNameFromUrl,
} from './utils';
import { UploadStage } from './FileUpload';

export interface ExternalUrlUploadProps {
  acceptedTypes?: AcceptedFileType[];
  isMediaLibrary?: boolean;
  onFileUploaded?: (fileMetadata: {
    id: string;
    name: string;
    url: string;
    size: number;
    type: string;
    provider: string;
    createdAt: string;
  }) => void;
  onError?: (error: string) => void;
  onProgressChange?: (progress: number) => void;
  onStageChange?: (stage: UploadStage) => void;
  onReset?: () => void;
}

const ExternalUrlUpload: React.FC<ExternalUrlUploadProps> = ({
  acceptedTypes = [],
  isMediaLibrary = false,
  onFileUploaded,
  onError,
  onProgressChange,
  onStageChange,
  onReset,
}) => {
  const { t } = useTranslation('admin');

  // Determine available types for selection
  const availableTypes = React.useMemo(() => {
    if (acceptedTypes.length === 0) {
      // Show all types if no types specified
      return Object.keys(FILE_TYPE_CATEGORIES) as FileTypeCategory[];
    }
    // Filter to only show accepted types that exist in FILE_TYPE_CATEGORIES
    return acceptedTypes.filter((type) => type in FILE_TYPE_CATEGORIES) as FileTypeCategory[];
  }, [acceptedTypes]);

  // Determine if we need file type selection
  const needsFileTypeSelection = availableTypes.length > 1 || availableTypes.length === 0;
  const singleType = availableTypes.length === 1 ? availableTypes[0] : null;

  // Create validation schema for external URL form
  const urlFormSchema = React.useMemo(() => {
    const urlSchema = z.url(t('fileUpload.externalUrl.validation.url'));

    const baseSchema = z.object({
      url: urlSchema,
    });

    if (needsFileTypeSelection) {
      // Create enum from available types - fileType is required
      const typeEnum = z.enum(availableTypes as [FileTypeCategory, ...FileTypeCategory[]], {
        message: t('fileUpload.externalUrl.validation.fileType'),
      });
      return baseSchema.extend({
        fileType: typeEnum,
      });
    }

    // If single type, fileType is optional but will be set to singleType
    return baseSchema.extend({
      fileType: z.string().optional(),
    });
  }, [needsFileTypeSelection, availableTypes, t]);

  type UrlFormData = z.infer<typeof urlFormSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    reset,
  } = useForm<UrlFormData>({
    resolver: zodResolver(urlFormSchema),
    mode: 'onChange',
    defaultValues: {
      fileType: singleType || undefined,
    },
  });

  const selectedFileType = watch('fileType') || singleType;

  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadStage, setUploadStage] = React.useState<UploadStage>('idle');
  const [error, setError] = React.useState<string | null>(null);

  // Set default file type if single type is provided
  React.useEffect(() => {
    if (singleType && !selectedFileType) {
      setValue('fileType', singleType);
    }
  }, [singleType, selectedFileType, setValue]);

  const updateProgress = (progress: number) => {
    setUploadProgress(progress);
    onProgressChange?.(progress);
  };

  const updateStage = (stage: UploadStage) => {
    setUploadStage(stage);
    onStageChange?.(stage);
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    onError?.(errorMessage);
  };

  const handleExternalUrlSubmit = async (data: UrlFormData) => {
    setError(null);
    updateStage('saving-metadata');
    updateProgress(50);

    try {
      // Determine the selected type
      const selectedType = (data.fileType || singleType) as FileTypeCategory | null;

      if (!selectedType) {
        throw new Error(t('fileUpload.externalUrl.validation.fileType'));
      }

      // Map FileTypeCategory to AcceptedFileType
      // If acceptedTypes is empty, treat selectedType as the accepted type
      const acceptedType =
        acceptedTypes.length > 0
          ? acceptedTypes.find((type) => type === selectedType) || (selectedType as AcceptedFileType)
          : (selectedType as AcceptedFileType);

      // Validate URL based on type
      if (acceptedType === AcceptedFileType.YOUTUBE) {
        const isValidYouTube = validateYouTubeUrl(data.url);
        if (!isValidYouTube) {
          throw new Error(t('fileUpload.externalUrl.validation.invalidYouTube'));
        }
      } else {
        const isValidType = await validateUrlFileType(data.url, acceptedType);
        if (!isValidType) {
          const category = FILE_TYPE_CATEGORIES[selectedType];
          const expectedMimeType = category.mimeTypes[0];
          throw new Error(t('fileUpload.externalUrl.validation.invalidFileType', { type: expectedMimeType }));
        }
      }

      // Determine MIME type and provider
      let mimeType: string;
      let provider: 's3' | 'external' | 'youtube';

      if (acceptedType === AcceptedFileType.YOUTUBE) {
        mimeType = 'video/youtube';
        provider = 'youtube';
      } else {
        mimeType = getMimeTypeFromCategory(selectedType);
        provider = 'external';
      }

      // Get file name from URL
      const fileName = getFileNameFromUrl(data.url, acceptedType);

      // Save file metadata to backend
      updateProgress(75);

      const fileMetadata = await filesApi.saveFileMetadata({
        name: fileName,
        url: data.url,
        size: 0, // External files don't have a known size
        type: mimeType,
        provider,
        isMediaLibrary,
      });

      updateProgress(100);
      updateStage('completed');

      // Call the callback with file metadata
      if (onFileUploaded) {
        onFileUploaded(fileMetadata);
      }

      addToast({
        title: t('fileUpload.externalUrl.success.title'),
        description: t('fileUpload.externalUrl.success.description'),
        color: 'success',
      });

      // Reset form
      reset();
      onReset?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('fileUpload.externalUrl.error.saveFailed');
      console.error('External URL save error:', err);
      addToast({
        title: t('fileUpload.externalUrl.error.title'),
        description: errorMessage,
        color: 'danger',
      });
      handleError(errorMessage);
      updateStage('error');
    }
  };

  const isUploading = ['getting-url', 'uploading', 'saving-metadata'].includes(uploadStage);
  const isCompleted = uploadStage === 'completed';
  const hasError = uploadStage === 'error';

  return (
    <form onSubmit={handleSubmit(handleExternalUrlSubmit)} className="space-y-4">
      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
        <div className="flex flex-col items-center space-y-4">
          <LinkIcon className="w-8 h-8 text-gray-400" />
          <div className="w-full space-y-4">
            {/* File Type Select (only if multiple types or empty array) */}
            {needsFileTypeSelection && (
              <Select
                label={t('fileUpload.externalUrl.fileType.label')}
                placeholder={t('fileUpload.externalUrl.fileType.placeholder')}
                isRequired
                selectedKeys={selectedFileType ? [selectedFileType] : []}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as FileTypeCategory;
                  setValue('fileType', selected);
                }}
                isInvalid={!!errors.fileType}
                errorMessage={errors.fileType?.message}
                variant="bordered"
              >
                {availableTypes.map((key) => {
                  const category = FILE_TYPE_CATEGORIES[key];
                  return <SelectItem key={key}>{category.label}</SelectItem>;
                })}
              </Select>
            )}

            {/* File Type Display (if single type - disabled) */}
            {singleType && !needsFileTypeSelection && (
              <Input
                label={t('fileUpload.externalUrl.fileType.label')}
                value={FILE_TYPE_CATEGORIES[singleType].label}
                isDisabled
                variant="bordered"
              />
            )}

            {/* URL Input */}
            <Input
              {...register('url')}
              type="url"
              label={t('fileUpload.externalUrl.url.label')}
              placeholder={
                selectedFileType === 'youtube'
                  ? t('fileUpload.externalUrl.url.youtubePlaceholder', {
                      defaultValue: 'https://www.youtube.com/watch?v=...',
                    })
                  : t('fileUpload.externalUrl.url.placeholder')
              }
              isRequired
              isInvalid={!!errors.url}
              errorMessage={errors.url?.message}
              variant="bordered"
              startContent={<LinkIcon className="w-4 h-4 text-gray-400" />}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              color="primary"
              isLoading={isSubmitting || isUploading}
              className="w-full"
              disabled={isSubmitting || isUploading}
            >
              {isSubmitting || isUploading
                ? t('fileUpload.externalUrl.button.saving')
                : t('fileUpload.externalUrl.button.save')}
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        {isUploading && (
          <div className="mt-4 space-y-2">
            <Progress
              aria-label="Save progress"
              value={uploadProgress}
              className="w-full"
              color="primary"
              showValueLabel
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {uploadStage === 'saving-metadata' && t('fileUpload.externalUrl.progress.saving')}
            </p>
          </div>
        )}

        {/* Completed State */}
        {isCompleted && (
          <div className="mt-4 flex items-center justify-center gap-2 text-green-600 dark:text-green-400">
            <CheckCircle2 className="w-5 h-5" />
            <span className="text-sm font-medium">{t('fileUpload.externalUrl.completed')}</span>
          </div>
        )}

        {/* Error State */}
        {hasError && error && (
          <div className="mt-4 text-red-600 dark:text-red-400">
            <p className="text-sm">{error}</p>
          </div>
        )}
      </div>
    </form>
  );
};

export default ExternalUrlUpload;
